/**
 * Plot configuration state. This is the new home of the
 * blob's `plot.state` object - the user-facing chart configuration that the
 * plot controls write and the renderer (§10.8) reads. It is a single atom (not
 * a bag of fine-grained atoms) because it is serialized to the URL / presets
 * and reset to defaults as one cohesive unit, exactly like the blob.
 *
 * Runtime plot state from the blob - `instance`, `lastData`, `isFetching`,
 * caches - is NOT here; that's the renderer's concern and lands with §10.8.
 *
 * Derived signals live in their own `derivedSignals` atom (§10.4), not inside
 * this object, so there is a single source for them. They still behave as
 * plottable pseudo-columns; the rename/remove cascades into this state are the
 * `stripDerivedFromPlot` / `renameDerivedInPlot` helpers below.
 */

import { Atom, activeDataSource, selectedShip, allColumns } from './store.ts';
import { syncToUrl } from './url-sync.ts';
import { preferences, rememberPreference } from './preferences.ts';
import {
  periodComparison,
  clearPeriodComparison,
  resumeSuspendedPeriodComparison,
  stopPeriodComparison,
} from './period-comparison.ts';
import { detectLatLon, isNumericType, isTemporalType } from '../utils/columns.ts';
import {
  DISCRETE_SCALE,
  DIVERGING_DEFAULT,
  DIVERGING_SCALES,
  isDiscreteScale,
} from '../features/plot/plot-constants.ts';
import {
  doPlot,
  rerenderChartFromLast,
  maybeFetchLegsAndRender,
  maybeFetchChangeoversAndRender,
  clearLegsOverlay,
  clearChangeoversOverlay,
  hidePeriodComparisonDisplay,
} from '../features/plot/plot-actions.ts';
import type { BoxMode, CorrMethod, CorrOrder, HeatmapAgg, NumberAgg, PieAgg, PieMode, PlotType, TableGroup, TableMode } from '../api/types.ts';
import { rewriteNumberSignal } from '../features/plot/number-config.ts';
import {
  GAUGE_MAX_DIALS,
  dialReady,
  newGaugeDial,
  rewriteGaugeSignal,
  type GaugeDialState,
  type GaugeLayout,
} from '../features/plot/gauge-config.ts';
import {
  SANKEY_DISPLAY_DEFAULT,
  nodeReady,
  rewriteSankeySignal,
  sankeyProblem,
  seedSankey,
  tidyNodes,
  valuesForRequest,
  type SankeyDisplay,
  type SankeyNodeState,
} from '../features/plot/sankey-config.ts';
import { validateNumberFormula } from '../features/plot/number-formula.ts';
import {
  TABLE_MAX_COLUMNS,
  TABLE_PAGE_SIZES,
  TABLE_PAGE_SIZE_DEFAULT,
  TABLE_MAX_MEASURES,
  isTableReady,
  renameInTable,
  seedMeasures,
  stripFromTable,
  type TableMeasureState,
} from '../features/plot/table-config.ts';
import { sourceHasHistory } from './data-sources.ts';

export type SeriesAxis = 'left' | 'right';

/** Multi-ship layout: one chart with every ship, or one panel per ship. */
export type PlotViewMode = 'overlay' | 'grid';

/**
 * How colour is allocated when more than one ship is plotted.
 *
 * - `hue-signal` - colour family per signal, lightness per ship. The default:
 *   signals usually outnumber ships, and hue is the stronger channel.
 * - `hue-ship`   - colour family per ship, lightness per signal.
 * - `focus`      - every ship takes the signal's colour; the focused ship draws
 *   solid and the rest fade back.
 * - `unique`     - each ship+signal line takes the next palette colour.
 *
 * Single-ship plots ignore this entirely and keep today's per-signal palette.
 */
export type PlotColorMode = 'hue-signal' | 'hue-ship' | 'focus' | 'unique';

export const PLOT_COLOR_MODES: Array<{ value: PlotColorMode; label: string }> = [
  { value: 'hue-signal', label: 'Colour by signal, shade by ship' },
  { value: 'hue-ship', label: 'Colour by ship, shade by signal' },
  { value: 'focus', label: 'Colour by signal, focus one ship' },
  { value: 'unique', label: 'Unique colour per line' },
];

export interface PlotStyle {
  connect_gaps: boolean;
  show_points: boolean;
  line_width: number;
  show_thresholds: boolean;
  log_scale: boolean;
  scatter_alpha: number;
  scatter_point_size: number;
  show_diagonal: boolean;
  /** Multi-ship colour allocation. Ignored when only one ship is plotted. */
  color_mode: PlotColorMode;
  /**
   * Heatmap colour ramp shape. `auto` - the default - means log for density
   * and linear for an aggregate: cell counts are viciously skewed (a handful of
   * dense cells, a long tail), and a linear ramp paints one hot square and 3000
   * indistinguishable dark ones.
   */
  heat_scale: HeatScale;
  /**
   * Size, in px, of the text the chart draws: axis values and labels, the
   * numbers inside a correlation cell, the legend and the tooltip.
   *
   * One setting for every plot type rather than one per chart: the charts sit
   * side by side in a grid and on a dashboard, and text that changed size
   * between them would read as a mistake. 13 rather than uPlot's stock 12 -
   * these charts are read on the wall as often as on the desk.
   */
  font_size: number;
  /**
   * Correlation matrix: draw only the lower triangle and the diagonal.
   *
   * Off by default - the full square is the familiar shape and every cell is
   * hoverable - but a matrix is symmetric, so half of it is the same numbers
   * twice, and past a dozen signals the halved version is markedly easier to
   * read along a row.
   */
  corr_triangle: boolean;
  /**
   * Pie: draw a ring with the total in the middle rather than solid wedges.
   *
   * On by default - arc lengths compare more accurately than wedge angles, and
   * the hole is where the total goes - but a pie is the shape some readers
   * expect, and it is theirs to pick.
   */
  pie_donut: boolean;
  /**
   * Hover focus: dim every series but the one under the pointer. Off by
   * default - on a dense chart it fades most of what you are looking at the
   * moment the pointer crosses the plot area, and the tooltip already says
   * which series is which.
   */
  focus_hover: boolean;
  /**
   * Grid view: one Y range shared by every panel. On by default - independent
   * scales make two ships look identical when their values differ tenfold,
   * which is the failure mode people do not notice.
   */
  /**
   * uPlot's legend under the chart: the series rows, each a click away from
   * hiding its line. Off by default, and it only ever appears with two or more
   * series anyway - but on a crowded chart, or one being screenshotted, the
   * rows are the first thing somebody wants back.
   */
  show_legend: boolean;
  /**
   * The "Select all / Deselect all" row inside that legend. Separate from the
   * legend itself because the two are wanted apart: keep the rows to read which
   * colour is which, drop the buttons that are only useful while isolating a
   * series. Inert while `show_legend` is off - there is nothing to put them in.
   */
  legend_tools: boolean;
  grid_shared_y: boolean;
  /**
   * Grid view: how many panels sit on one row, or null for the automatic rule
   * (`gridColumns` - three on a wide screen, two on a normal one, one when even
   * two would be too narrow). Automatic is the default because it is what every
   * existing link and saved view means; a number is a deliberate choice and is
   * honoured at any width, including one that squeezes.
   */
  grid_columns: number | null;
  /**
   * Range bars along the X and Y axes that narrow the view without a refetch
   * (`render/precision-zoom.ts`). Line and Scatter, single chart.
   */
  precision_zoom: boolean;
  /**
   * Live line charts: where the newest readings show - beside each line's end,
   * large across the middle, or on a NOW line in the middle of an axis that
   * runs a window either side of now (`render/last-value-tags.ts`).
   */
  live_layout: LiveLayout;
  /** `live_layout: 'now'`: the NOW label on top of the line. */
  live_now_label: boolean;
  /**
   * How a live chart's axis moves between ticks: every frame (`smooth`), once
   * a second (`steps`), or not at all - it jumps to now when new rows land
   * (`off`). The chart's own choice; the system's reduced-motion setting no
   * longer decides it.
   */
  live_motion: LiveMotion;
}

/** Where a live line chart shows its newest readings. */
export type LiveLayout = 'end' | 'center' | 'now';
/** How a live chart's axis moves between ticks. */
export type LiveMotion = 'smooth' | 'steps' | 'off';

export interface AxisBounds {
  x_min: number | null;
  x_max: number | null;
  y_min: number | null;
  y_max: number | null;
  yr_min: number | null;
  yr_max: number | null;
}

/**
 * What one Y axis title says. `auto` is the first three series and "(+N)";
 * `signals` names only the picked series on that axis (none left on it reads
 * as auto); `text` is the user's own words; `off` draws no title.
 */
export type AxisTitleMode = 'auto' | 'signals' | 'text' | 'off';

export interface AxisTitle {
  mode: AxisTitleMode;
  signals: string[];
  text: string;
}

export interface AxisTitles {
  y: AxisTitle;
  yr: AxisTitle;
}

export function autoAxisTitle(): AxisTitle {
  return { mode: 'auto', signals: [], text: '' };
}

/** A curve fit attached to a series (§10.8 renders it; controls open/edit it). */
export interface Fit {
  series: string;
  kind: string;
  params: Record<string, number>;
  color?: string;
}

export interface PlotState {
  plot_type: PlotType;
  number_mode: 'signal' | 'formula';
  number_column: string;
  number_agg: NumberAgg;
  number_expression: string;
  number_label: string;
  number_unit: string;
  number_decimals: number;
  /** Gauge: one dial per sensor, each a Number's value with a scale and zones. */
  gauge_dials: GaugeDialState[];
  /** Gauge with several ships: panels per ship, per dial, or a ships x dials matrix. */
  gauge_layout: GaugeLayout;
  /** Sankey: columns of nodes and the links between them (docs/plans/sankey.md). */
  sankey_nodes: SankeyNodeState[];
  /** Sankey: the one unit and decimal places of every node, and the moving dashes. */
  sankey_display: SankeyDisplay;
  /** Multi-ship layout. Has no effect while a single ship is plotted. */
  view_mode: PlotViewMode;
  x_column: string;
  y_left: string[];
  y_right: string[];
  show_legs: boolean;
  show_changeovers: boolean;
  color_by: string;
  color_scale: string;
  hist_columns: string[];
  hist_bins: number;
  /**
   * Heatmap: the second binned axis, and what colours a cell.
   *
   * `z_column` empty means density - the row count per cell, which is the thing
   * a saturated scatter cannot show. With one set, the cell carries `z_agg` of
   * that column instead.
   */
  y_column: string;
  z_column: string;
  /**
   * Null means colour by density. Held separately from `z_column` rather than
   * inferred from it: picking "Mean of…" has to survive the moment before a
   * column is chosen, or the control snaps back to density and the column
   * picker it was supposed to reveal never appears.
   */
  z_agg: HeatmapAgg | null;
  /**
   * Resolution hint, or null for automatic (the server derives the width from
   * the data with Freedman-Diaconis). Either way the width it implies is
   * rounded to a nice step, so this is never a promise of a cell count.
   */
  heat_bins: number | null;
  /** Explicit cell size in data units. Null = derived from `heat_bins`. */
  heat_x_step: number | null;
  heat_y_step: number | null;
  /**
   * Correlation matrix: the signals to measure against each other, in the order
   * they are drawn - the picker reorders them, and `corr_order: 'cluster'` lets
   * the server reorder them again by similarity.
   */
  corr_columns: string[];
  corr_method: CorrMethod;
  corr_order: CorrOrder;
  /**
   * The ramp that was in use before switching to Correlation swapped it for a
   * diverging one, or null when nothing was swapped.
   *
   * Held so leaving Correlation puts the old ramp back: a density heatmap on a
   * diverging ramp reads low counts as "negative", which is exactly the
   * confusion the swap exists to prevent in the other direction. Cleared the
   * moment a ramp is chosen by hand - that choice is not ours to undo. Not
   * serialized: a link describes the chart it opens, not how it was reached.
   */
  ramp_before_corr: string | null;
  /**
   * Pie. `signals` slices a whole by signal (`pie_columns`, each `pie_agg`ed
   * over the range); `bands` cuts one signal (`pie_band_column`) into value
   * bands and slices by how many rows fell in each.
   *
   * Both modes' inputs are kept while the other is showing, so flipping the
   * switch to look and flipping back loses nothing.
   */
  pie_mode: PieMode;
  pie_columns: string[];
  pie_agg: PieAgg;
  pie_band_column: string;
  /** About how many bands when the cuts are chosen from the data. */
  pie_bands: number;
  /** Explicit cut points between bands, increasing. Null = chosen from the data. */
  pie_band_edges: number[] | null;
  /**
   * Box plot. `signals` draws one box per signal (`box_columns`); `groups`
   * draws one signal (`box_column`) split by the values of `box_group_column`.
   * Both modes' inputs are kept while the other is showing, like the pie's.
   */
  box_mode: BoxMode;
  box_columns: string[];
  box_column: string;
  box_group_column: string;
  /**
   * Table. `readings` is the data itself - the time, then `table_columns` as
   * they are, like a spreadsheet. `summary` is statistics: rows grouped by up
   * to two levels (none is one summary row), one column per measure.
   * `table_sort` is `<column id>:<asc|desc>` - `k0`, `k1`
   * for the group keys, `ship`, `n`, `m<i>` for a measure - or empty for the
   * order the rows came in.
   */
  table_mode: TableMode;
  table_columns: string[];
  table_groups: TableGroup[];
  table_measures: TableMeasureState[];
  table_sort: string;
  /** Readings: the page shown (0-based) and readings per page. */
  table_page: number;
  table_page_size: number;
  /** Map plot: latitude / longitude columns and basemap toggles. */
  lat_column: string;
  lon_column: string;
  map_nautical: boolean;
  map_show_track: boolean;
  style: PlotStyle;
  /** Per-column color overrides, `{ colName: '#rrggbb' }`. */
  custom_colors: Record<string, string>;
  fits: Fit[];
  /** User-set axis bounds; null on a field = auto. */
  axis_bounds: AxisBounds;
  /** What the Y axis titles say (line and scatter). */
  axis_titles: AxisTitles;
}

export const LINE_WIDTH_MIN = 1;
export const LINE_WIDTH_MAX = 8;
export const POINT_SIZE_MIN = 1;
export const POINT_SIZE_MAX = 8;
/**
 * Chart text size bounds. The floor is where axis values stop being readable at
 * arm's length; the ceiling is where they start eating the plot area.
 */
export const FONT_SIZE_MIN = 9;
export const FONT_SIZE_MAX = 20;
export const FONT_SIZE_DEFAULT = 13;

export const HIST_BINS_MIN = 5;
export const HIST_BINS_MAX = 200;
/** Matches the backend's `HeatmapRequest.bins` bounds. */
export const HEAT_BINS_MIN = 5;
export const HEAT_BINS_MAX = 200;

/** Two signals is the smallest thing a matrix can say anything about. */
export const CORR_MIN_COLUMNS = 2;
/** Mirrors the backend's `CORR_MAX_COLUMNS` - past this the labels stop fitting. */
export const CORR_MAX_COLUMNS = 40;

/** A whole needs at least two parts to be divided into. */
export const PIE_MIN_COLUMNS = 2;
/** Mirrors the backend's `PIE_MAX_SLICES` - past this the slices are slivers. */
export const PIE_MAX_COLUMNS = 15;
/** Boxes side by side: past this many they are slivers. Mirrors backend `BOX_MAX_BOXES`. */
export const BOX_MAX_COLUMNS = 15;
/** Mirrors the backend's `PIE_MAX_BANDS`. */
export const PIE_BANDS_MIN = 2;
export const PIE_BANDS_MAX = 12;
export const PIE_BANDS_DEFAULT = 5;

export const PIE_AGGS: Array<{ value: PieAgg; label: string }> = [
  { value: 'avg', label: 'Mean' },
  { value: 'sum', label: 'Sum' },
];

export const CORR_METHODS: Array<{ value: CorrMethod; label: string }> = [
  { value: 'pearson', label: 'Pearson (linear)' },
  { value: 'spearman', label: 'Spearman (rank)' },
];

export type HeatScale = 'auto' | 'linear' | 'log' | 'sqrt';

export const HEAT_SCALES: Array<{ value: HeatScale; label: string }> = [
  { value: 'auto', label: 'Auto (log for density)' },
  { value: 'linear', label: 'Linear' },
  { value: 'log', label: 'Log' },
  { value: 'sqrt', label: 'Square root' },
];

/** Bins move in fives - one at a time is 195 clicks from end to end. */
export const HEAT_BINS_STEP = 5;

/** Where the resolution slider starts when Auto is switched off. */
export const HEAT_BINS_DEFAULT = 40;

/**
 * What a heatmap cell means, as one list.
 *
 * `density` is the no-column case (colour by row count); everything else is an
 * aggregate of a chosen column. Presented together because that is the one
 * question being asked - "what am I looking at in each cell?" - and because
 * every other tool asks it this way.
 */
export const HEAT_CELL_MODES: Array<{ value: HeatmapAgg | 'density'; label: string }> = [
  { value: 'density', label: 'Row count (density)' },
  { value: 'avg', label: 'Mean of…' },
  { value: 'median', label: 'Median of…' },
  { value: 'min', label: 'Min of…' },
  { value: 'max', label: 'Max of…' },
  { value: 'sum', label: 'Sum of…' },
  { value: 'count', label: 'Non-null count of…' },
];

/** The X column that means "time". Every source names it `timestampNDC`
 * (the relation contract in `backend/sources/base.py`). */
export function timeColumn(): string {
  return 'timestampNDC';
}

function defaultStyle(): PlotStyle {
  return {
    connect_gaps: false,
    show_points: false,
    line_width: 2,
    show_thresholds: true,
    log_scale: false,
    scatter_alpha: 0.7,
    scatter_point_size: 3,
    show_diagonal: false,
    color_mode: 'hue-signal',
    heat_scale: 'auto',
    font_size: FONT_SIZE_DEFAULT,
    corr_triangle: false,
    pie_donut: true,
    focus_hover: false,
    show_legend: false,
    legend_tools: true,
    grid_shared_y: true,
    grid_columns: null,
    precision_zoom: false,
    live_layout: 'now',
    live_now_label: true,
    live_motion: 'smooth',
  };
}

function defaultAxisBounds(): AxisBounds {
  return {
    x_min: null, x_max: null,
    y_min: null, y_max: null,
    yr_min: null, yr_max: null,
  };
}

export function defaultPlotState(): PlotState {
  return {
    plot_type: 'line',
    view_mode: 'overlay',
    x_column: 'timestampNDC',
    y_left: [],
    y_right: [],
    show_legs: false,
    show_changeovers: false,
    color_by: '',
    color_scale: 'Viridis',
    hist_columns: [],
    hist_bins: 40,
    y_column: '',
    z_column: '',
    z_agg: null,
    heat_bins: null,
    heat_x_step: null,
    heat_y_step: null,
    corr_columns: [],
    corr_method: 'pearson',
    corr_order: 'given',
    number_mode: 'signal',
    number_column: '',
    number_agg: 'latest',
    number_expression: '',
    number_label: '',
    number_unit: '',
    number_decimals: 2,
    gauge_dials: [],
    gauge_layout: 'ship',
    sankey_nodes: [],
    sankey_display: { ...SANKEY_DISPLAY_DEFAULT },
    pie_mode: 'signals',
    pie_columns: [],
    pie_agg: 'avg',
    pie_band_column: '',
    pie_bands: PIE_BANDS_DEFAULT,
    pie_band_edges: null,
    box_mode: 'signals',
    box_columns: [],
    box_column: '',
    box_group_column: '',
    table_mode: 'readings',
    table_columns: [],
    table_groups: [],
    table_measures: [],
    table_sort: '',
    table_page: 0,
    table_page_size: TABLE_PAGE_SIZE_DEFAULT,
    ramp_before_corr: null,
    lat_column: '',
    lon_column: '',
    map_nautical: false,
    map_show_track: true,
    style: defaultStyle(),
    custom_colors: {},
    fits: [],
    axis_bounds: defaultAxisBounds(),
    axis_titles: { y: autoAxisTitle(), yr: autoAxisTitle() },
  };
}

/**
 * Style keys that are *taste* and get remembered on the account: how big the
 * text is, how the points look, which chrome is showing.
 *
 * The ones left out - `connect_gaps`, `show_thresholds`, `log_scale`,
 * `show_diagonal` - read as decisions about the data rather than about the look
 * of it, and belong to the chart that made them. The test is the one the ⚙ menu
 * already prints on itself: everything here redraws from cache, but not
 * everything that redraws from cache is taste.
 */
export const REMEMBERED_STYLE_KEYS: ReadonlySet<keyof PlotStyle> = new Set([
  'font_size',
  'line_width',
  'scatter_alpha',
  'scatter_point_size',
  'show_points',
  'show_legend',
  'legend_tools',
  'focus_hover',
  'grid_shared_y',
  'grid_columns',
  'precision_zoom',
  'heat_scale',
  'corr_triangle',
  'pie_donut',
  'color_mode',
  'live_layout',
  'live_now_label',
  'live_motion',
]);

/**
 * What shape each remembered setting is, so a stored value can be checked
 * against the key rather than against whatever the chart currently holds.
 *
 * Keyed explicitly because one of them is nullable: `grid_columns: null` means
 * "lay the grid out automatically", and inferring the type from the current
 * value would make that unstorable the moment somebody picked a number.
 */
const PREFERENCE_KINDS: Record<string, 'number' | 'boolean' | 'string' | 'number-or-null'> = {
  font_size: 'number',
  line_width: 'number',
  scatter_alpha: 'number',
  scatter_point_size: 'number',
  show_points: 'boolean',
  show_legend: 'boolean',
  legend_tools: 'boolean',
  focus_hover: 'boolean',
  grid_shared_y: 'boolean',
  precision_zoom: 'boolean',
  corr_triangle: 'boolean',
  pie_donut: 'boolean',
  heat_scale: 'string',
  color_mode: 'string',
  grid_columns: 'number-or-null',
  live_layout: 'string',
  live_now_label: 'boolean',
  live_motion: 'string',
};

/** One stored value, applied over the current one only when it is the right shape. */
function coerce(key: string, value: unknown, fallback: unknown): unknown {
  const kind = PREFERENCE_KINDS[key];
  const isNumber = typeof value === 'number' && Number.isFinite(value);
  if (kind === 'number') return isNumber ? value : fallback;
  if (kind === 'boolean') return typeof value === 'boolean' ? value : fallback;
  if (kind === 'string') return typeof value === 'string' && value ? value : fallback;
  if (kind === 'number-or-null') {
    if (value === null) return null;
    return isNumber ? value : fallback;
  }
  return fallback;
}

/**
 * A plot state with this person's remembered appearance applied over the code
 * defaults.
 *
 * Only what they actually chose is applied, and only where the shape matches -
 * a corrupt or stale mirror should cost the setting, not the boot.
 */
export function applyChartPreferences(
  base: PlotState,
  chart: Record<string, unknown> | undefined,
): PlotState {
  if (!chart || typeof chart !== 'object') return base;
  const style = { ...base.style };
  for (const key of REMEMBERED_STYLE_KEYS) {
    if (!(key in chart)) continue;
    (style as Record<string, unknown>)[key] = coerce(
      key,
      chart[key],
      base.style[key] as unknown,
    );
  }
  const color_scale =
    typeof chart.color_scale === 'string' && chart.color_scale
      ? chart.color_scale
      : base.color_scale;
  return { ...base, style, color_scale };
}

/**
 * The state a fresh chart starts from: code defaults, then the person's
 * preferences.
 *
 * This is what the atom is seeded with, *before* `restoreFromUrl` runs, which is
 * the whole of the precedence rule: a link that names a setting overrides it, a
 * link that is silent inherits it. Read from the synchronous `localStorage`
 * mirror, so the first paint is already right and no request has to have come
 * back.
 */
export function seededPlotState(): PlotState {
  return applyChartPreferences(defaultPlotState(), preferences.value.chart);
}

export const plotState = new Atom<PlotState>(seededPlotState());

/** Remember one appearance change against the account (optimistic; see
 *  `state/preferences.ts`). Called only from the setters a person drives. */
function remember(key: string, value: unknown): void {
  rememberPreference({ chart: { [key]: value } });
}

/** Immutable update: replace the state via a producer, then notify. */
function update(producer: (prev: PlotState) => PlotState): void {
  plotState.set(producer(plotState.value));
}

/* ── Plot type ──────────────────────────────────────────── */

export function setPlotType(t: PlotType): void {
  if (t === plotState.value.plot_type) return;
  update((s) => {
    const next = { ...s, plot_type: t };
    if (t === 'number' && !s.number_column) next.number_column = s.y_left[0] ?? s.y_right[0] ?? '';
    // A gauge opens on what was showing: a Number carries over whole - its
    // value, label, unit and places - else a dial per signal of the chart.
    if (t === 'gauge' && !s.gauge_dials.length) {
      if (s.plot_type === 'number' && (s.number_mode === 'formula' ? s.number_expression : s.number_column)) {
        next.gauge_dials = [{
          ...newGaugeDial(s.number_column),
          mode: s.number_mode, agg: s.number_agg, expression: s.number_expression,
          label: s.number_label, unit: s.number_unit, decimals: s.number_decimals,
        }];
      } else {
        const shown = [...s.y_left, ...s.y_right].slice(0, 4);
        next.gauge_dials = shown.length ? shown.map((c) => newGaugeDial(c)) : [newGaugeDial()];
      }
    }
    // A Sankey opens on what was showing: those signals in the first column,
    // flowing into one Sum of inputs.
    if (t === 'sankey' && !s.sankey_nodes.length) {
      const shown = [...s.y_left, ...s.y_right];
      const from = shown.length ? shown : s.pie_columns.length ? s.pie_columns : s.gauge_dials.map((d) => d.column);
      next.sankey_nodes = seedSankey(from.filter(Boolean).slice(0, 8));
    }
    // A table opens on what the chart was showing: its signals as readings,
    // and their averages should the person switch to a summary.
    if (t === 'table') {
      const shown = [...s.y_left, ...s.y_right];
      if (!s.table_columns.length) next.table_columns = shown.slice(0, 10);
      if (!s.table_measures.length) next.table_measures = seedMeasures(shown);
    }
    // Switching to Correlation: a signed matrix on a sequential ramp hides the
    // sign, so move to a diverging one. Only when the current ramp is not
    // already diverging - a deliberate choice made here is left alone - and the
    // old one is kept so leaving puts it back.
    if (t === 'correlation' && !DIVERGING_SCALES.has(s.color_scale)) {
      next.ramp_before_corr = s.color_scale;
      next.color_scale = DIVERGING_DEFAULT;
    } else if (t !== 'correlation' && s.ramp_before_corr) {
      if (s.color_scale === DIVERGING_DEFAULT) next.color_scale = s.ramp_before_corr;
      next.ramp_before_corr = null;
    }
    // Switching to Map: seed lat/lon from the schema by name if not set yet.
    if (t === 'map' && (!s.lat_column || !s.lon_column)) {
      const { lat, lon } = detectLatLon(allColumns.value);
      if (!next.lat_column) next.lat_column = lat;
      if (!next.lon_column) next.lon_column = lon;
    }
    return next;
  });
  if (t !== 'line' && t !== 'scatter' && periodComparison.value) {
    stopPeriodComparison(`Period comparison paused. It is available for Line and Scatter, not ${t}.`);
    hidePeriodComparisonDisplay('Choose Line or Scatter to restore it.');
  } else if ((t === 'line' || t === 'scatter') && sourceHasHistory(activeDataSource.value)) {
    if (resumeSuspendedPeriodComparison()) rerenderChartFromLast();
  }
  syncToUrl();
}

/** Overlay ↔ grid. Layout only, so it redraws from cached data. */
export function setViewMode(mode: PlotViewMode): void {
  if (mode === plotState.value.view_mode) return;
  update((s) => ({ ...s, view_mode: mode }));
  rerenderChartFromLast();
  syncToUrl();
}

/* ── X axis ─────────────────────────────────────────────── */

export function setXColumn(col: string): void {
  update((s) => ({ ...s, x_column: col }));
  syncToUrl();
}

export function setYColumn(col: string): void {
  update((s) => ({ ...s, y_column: col }));
  syncToUrl();
}

/**
 * Heatmap colour source. Empty string = colour by cell density.
 *
 * The aggregate is left alone: clearing the column while "Mean of…" is selected
 * should leave the picker up waiting for another column, not silently switch
 * the chart's meaning.
 */
export function setZColumn(col: string): void {
  update((s) => ({ ...s, z_column: col }));
  syncToUrl();
}

/** Null switches back to density, and drops the column with it. */
export function setZAgg(agg: HeatmapAgg | null): void {
  update((s) => (agg ? { ...s, z_agg: agg } : { ...s, z_agg: null, z_column: '' }));
  syncToUrl();
}

/** Null asks the server to derive the resolution from the data. */
export function setHeatBins(n: number | null): void {
  const v =
    n === null ? null : Math.max(HEAT_BINS_MIN, Math.min(HEAT_BINS_MAX, Math.round(n)));
  update((s) => ({ ...s, heat_bins: v }));
  syncToUrl();
}

/**
 * Explicit cell size for one axis, or null to go back to a derived one.
 *
 * Set directly, this is the control every heatmap tool that people like offers:
 * "half a knot by a hundredth of a degree" is the question being asked, and a
 * bin count is only ever a proxy for it.
 */
export function setHeatStep(axis: 'x' | 'y', step: number | null): void {
  const v = step != null && Number.isFinite(step) && step > 0 ? step : null;
  update((s) => (axis === 'x' ? { ...s, heat_x_step: v } : { ...s, heat_y_step: v }));
  syncToUrl();
}

/** Set the X axis to the data source's time column (blob `setTimeAxis`). */
export function setTimeAxis(): void {
  setXColumn(timeColumn());
}

export function isTimeAxis(s: PlotState = plotState.value): boolean {
  return s.x_column === timeColumn();
}

/**
 * Whether the current configuration is plottable - a ship is selected and the
 * active plot type has its required series (blob `#plot-btn` disabled logic).
 * Shared so the plot button (§10.7) and the preset auto-plot (§10.11) agree.
 */
export function isPlotEnabled(s: PlotState = plotState.value): boolean {
  if (!selectedShip.value) return false;
  if (s.plot_type === 'number') {
    return s.number_mode === 'formula' ? validateNumberFormula(s.number_expression) === null : !!s.number_column;
  }
  if (s.plot_type === 'gauge') {
    return s.gauge_dials.length > 0 && s.gauge_dials.every(dialReady);
  }
  if (s.plot_type === 'sankey') {
    return s.sankey_nodes.every(nodeReady) && sankeyProblem(s.sankey_nodes) === null;
  }
  if (s.plot_type === 'line') return s.y_left.length + s.y_right.length > 0;
  if (s.plot_type === 'scatter') return !!s.x_column && s.y_left.length + s.y_right.length > 0;
  if (s.plot_type === 'heatmap') return !!s.x_column && !!s.y_column;
  if (s.plot_type === 'correlation') return s.corr_columns.length >= CORR_MIN_COLUMNS;
  if (s.plot_type === 'pie') {
    return s.pie_mode === 'bands'
      ? !!s.pie_band_column
      : s.pie_columns.length >= PIE_MIN_COLUMNS;
  }
  if (s.plot_type === 'box') {
    return s.box_mode === 'groups'
      ? !!s.box_column && !!s.box_group_column && s.box_column !== s.box_group_column
      : s.box_columns.length > 0;
  }
  if (s.plot_type === 'map') return !!s.lat_column && !!s.lon_column;
  if (s.plot_type === 'table') {
    return isTableReady(s.table_groups, s.table_measures, s.table_mode, s.table_columns);
  }
  return s.hist_columns.length > 0;
}

/* ── Y series (left/right; line and scatter both) ───────── */

/**
 * Add a column to an axis, removing it from the other axis first so a series
 * never lives on both (blob `addSeries`).
 */
export function addSeries(axis: SeriesAxis, col: string): void {
  if (!col) return;
  update((s) => {
    const other: SeriesAxis = axis === 'left' ? 'right' : 'left';
    const otherKey = `y_${other}` as const;
    const key = `y_${axis}` as const;
    const otherList = s[otherKey].filter((c) => c !== col);
    const list = s[key].includes(col) ? s[key] : [...s[key], col];
    return { ...s, [otherKey]: otherList, [key]: list };
  });
  syncToUrl();
}

export function removeSeries(axis: SeriesAxis, col: string): void {
  update((s) => {
    const key = `y_${axis}` as const;
    return { ...s, [key]: s[key].filter((c) => c !== col) };
  });
  syncToUrl();
}

export function reorderSeries(axis: SeriesAxis, from: number, to: number): void {
  update((s) => {
    const key = `y_${axis}` as const;
    const list = [...s[key]];
    if (from < 0 || from >= list.length || to < 0 || to >= list.length) return s;
    const moved = list.splice(from, 1)[0];
    if (moved === undefined) return s;
    list.splice(to, 0, moved);
    return { ...s, [key]: list };
  });
  rerenderChartFromLast();
  syncToUrl();
}

/** Add every plottable column not already used (blob `addAllToYLeft`). The
 *  candidate name list is supplied by the caller to avoid a circular import. */
export function addAllToYLeft(candidateNames: string[]): void {
  update((s) => {
    const used = new Set([...s.y_left, ...s.y_right, s.x_column]);
    const toAdd = candidateNames.filter((n) => !used.has(n));
    if (!toAdd.length) return s;
    return { ...s, y_left: [...s.y_left, ...toAdd] };
  });
  syncToUrl();
}

/** Clear every picked Y series across both axes. */
export function clearAllSeries(): void {
  update((s) => {
    if (!s.y_left.length && !s.y_right.length) return s;
    return { ...s, y_left: [], y_right: [] };
  });
  syncToUrl();
}

/** Replace the line selection with `cols` as Y-left (blob `plotSelectedCols`). */
export function setLineSelection(cols: string[]): void {
  update((s) => ({ ...s, plot_type: 'line', y_left: [...cols], y_right: [] }));
  syncToUrl();
}

/* ── Per-series color ───────────────────────────────────── */

export function setCustomColor(col: string, hex: string): void {
  update((s) => ({ ...s, custom_colors: { ...s.custom_colors, [col]: hex } }));
  rerenderChartFromLast();
  syncToUrl();
}

/* ── Scatter color-by + scale ───────────────────────────── */

/** A raw column that is neither numeric nor temporal → discrete-only coloring.
 *  Derived signals (absent from `allColumns`) are always numeric, so default
 *  false. */
function colorByIsCategorical(col: string): boolean {
  const info = allColumns.value.find((c) => c.name === col);
  return !!info && !isNumericType(info.type) && !isTemporalType(info.type);
}

export function setColorBy(col: string): void {
  update((s) => {
    const next = { ...s, color_by: col };
    // A categorical column can't use a continuous gradient - fall back to the
    // discrete (categorical) palette so the renderer and scale select agree.
    if (col && colorByIsCategorical(col) && !isDiscreteScale(next.color_scale)) {
      next.color_scale = DISCRETE_SCALE;
    }
    return next;
  });
  rerenderChartFromLast();
  syncToUrl();
}

export function clearColorBy(): void {
  setColorBy('');
}

export function setColorScale(name: string): void {
  // Chosen by hand, so there is nothing left to put back.
  update((s) => ({ ...s, color_scale: name, ramp_before_corr: null }));
  remember('color_scale', name);
  rerenderChartFromLast();
  syncToUrl();
}

/* ── Map (lat/lon + basemap toggles) ────────────────────── */

export function setLatColumn(col: string): void {
  update((s) => ({ ...s, lat_column: col }));
  syncToUrl();
}

export function setLonColumn(col: string): void {
  update((s) => ({ ...s, lon_column: col }));
  syncToUrl();
}

/** Toggle the OpenSeaMap nautical overlay - redraw in place, no refetch. */
export function setMapNautical(on: boolean): void {
  update((s) => ({ ...s, map_nautical: on }));
  rerenderChartFromLast();
  syncToUrl();
}

/** Toggle the connecting track polyline - redraw in place, no refetch. */
export function setMapShowTrack(on: boolean): void {
  update((s) => ({ ...s, map_show_track: on }));
  rerenderChartFromLast();
  syncToUrl();
}

/* ── Histogram ──────────────────────────────────────────── */

export function addHistCol(col: string): void {
  update((s) =>
    s.hist_columns.includes(col)
      ? s
      : { ...s, hist_columns: [...s.hist_columns, col] },
  );
  syncToUrl();
}

export function removeHistCol(col: string): void {
  update((s) => ({ ...s, hist_columns: s.hist_columns.filter((c) => c !== col) }));
  syncToUrl();
}

export function reorderHistCol(from: number, to: number): void {
  update((s) => {
    const list = [...s.hist_columns];
    if (from < 0 || from >= list.length || to < 0 || to >= list.length) return s;
    const moved = list.splice(from, 1)[0];
    if (moved === undefined) return s;
    list.splice(to, 0, moved);
    return { ...s, hist_columns: list };
  });
  rerenderChartFromLast();
  syncToUrl();
}

/** Chart text size, clamped to what stays readable and still fits. */
export function setFontSize(px: number): void {
  const v = Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, Math.round(px) || FONT_SIZE_DEFAULT));
  update((s) => ({ ...s, style: { ...s.style, font_size: v } }));
  remember('font_size', v);
  rerenderChartFromLast();
  syncToUrl();
}

/* ── Correlation matrix columns ─────────────────────────── */

export function addCorrCol(col: string): void {
  update((s) =>
    s.corr_columns.includes(col) || s.corr_columns.length >= CORR_MAX_COLUMNS
      ? s
      : { ...s, corr_columns: [...s.corr_columns, col] },
  );
  syncToUrl();
}

export function removeCorrCol(col: string): void {
  update((s) => ({ ...s, corr_columns: s.corr_columns.filter((c) => c !== col) }));
  syncToUrl();
}

/**
 * Move a signal within the matrix.
 *
 * Unlike the histogram's, this cannot redraw from cached data: the axis order
 * *is* the response's order, so the matrix is stale until it is fetched again.
 * Nothing is re-queried here - the Plot button lights up instead.
 */
export function reorderCorrCol(from: number, to: number): void {
  update((s) => {
    const list = [...s.corr_columns];
    if (from < 0 || from >= list.length || to < 0 || to >= list.length) return s;
    const moved = list.splice(from, 1)[0];
    if (moved === undefined) return s;
    list.splice(to, 0, moved);
    return { ...s, corr_columns: list };
  });
  syncToUrl();
}

export function setCorrMethod(method: CorrMethod): void {
  update((s) => ({ ...s, corr_method: method }));
  syncToUrl();
}

export function setCorrOrder(order: CorrOrder): void {
  update((s) => ({ ...s, corr_order: order }));
  syncToUrl();
}

/* ── Number ─────────────────────────────────────────────── */

export function setNumberField<K extends keyof Pick<PlotState,
  'number_mode' | 'number_column' | 'number_agg' | 'number_expression' |
  'number_label' | 'number_unit' | 'number_decimals'>>(key: K, value: PlotState[K]): void {
  update((s) => ({ ...s, [key]: value }));
  if (key === 'number_label' || key === 'number_unit' || key === 'number_decimals') rerenderChartFromLast();
  syncToUrl();
}

/* ── Gauge ──────────────────────────────────────────────── */

/**
 * Drawing settings - label, unit, places, scale, zones, layout - redraw what
 * is loaded; only a dial's value (signal, calculation, formula) needs Plot.
 */
const GAUGE_VALUE_KEYS: ReadonlySet<keyof GaugeDialState> = new Set(['mode', 'column', 'agg', 'expression']);

export function setGaugeDial(index: number, patch: Partial<GaugeDialState>): void {
  update((s) => ({
    ...s,
    gauge_dials: s.gauge_dials.map((d, i) => (i === index ? { ...d, ...patch } : d)),
  }));
  if (!Object.keys(patch).some((k) => GAUGE_VALUE_KEYS.has(k as keyof GaugeDialState))) rerenderChartFromLast();
  syncToUrl();
}

export function addGaugeDial(column = ''): void {
  update((s) =>
    s.gauge_dials.length >= GAUGE_MAX_DIALS ? s : { ...s, gauge_dials: [...s.gauge_dials, newGaugeDial(column)] },
  );
  syncToUrl();
}

/**
 * A copy of a dial, settings and all, straight after it - for the second
 * engine of a pair, where only the signal changes. It reads what the original
 * reads, so the loaded value draws at once.
 */
export function duplicateGaugeDial(index: number): void {
  update((s) => {
    const d = s.gauge_dials[index];
    if (!d || s.gauge_dials.length >= GAUGE_MAX_DIALS) return s;
    const dials = [...s.gauge_dials];
    dials.splice(index + 1, 0, { ...d, zones: d.zones.map((z) => ({ ...z })) });
    return { ...s, gauge_dials: dials };
  });
  rerenderChartFromLast();
  syncToUrl();
}

/** Removing a dial redraws: loaded values are matched to dials by `dialKey`. */
export function removeGaugeDial(index: number): void {
  update((s) => ({ ...s, gauge_dials: s.gauge_dials.filter((_, i) => i !== index) }));
  rerenderChartFromLast();
  syncToUrl();
}

/** Reorder dials. The loaded values move with them, so nothing is refetched. */
export function moveGaugeDial(from: number, to: number): void {
  const n = plotState.value.gauge_dials.length;
  if (from === to || from < 0 || to < 0 || from >= n || to >= n) return;
  update((s) => {
    const dials = [...s.gauge_dials];
    const [moved] = dials.splice(from, 1);
    dials.splice(to, 0, moved!);
    return { ...s, gauge_dials: dials };
  });
  rerenderChartFromLast();
  syncToUrl();
}

export function setGaugeLayout(layout: GaugeLayout): void {
  update((s) => ({ ...s, gauge_layout: layout }));
  rerenderChartFromLast();
  syncToUrl();
}

/* ── Sankey ─────────────────────────────────────────────── */

/**
 * Replace the Sankey's nodes. Only a change to what the nodes read (a signal,
 * an aggregate, a formula) waits for Plot; columns, links, labels and the
 * arithmetic nodes redraw what is loaded, because values are matched to nodes
 * by what they read.
 */
export function setSankeyNodes(nodes: SankeyNodeState[]): void {
  const before = JSON.stringify(valuesForRequest(plotState.value.sankey_nodes));
  const next = tidyNodes(nodes);
  update((s) => ({ ...s, sankey_nodes: next }));
  if (JSON.stringify(valuesForRequest(next)) === before) rerenderChartFromLast();
  syncToUrl();
}

export function setSankeyDisplay(patch: Partial<SankeyDisplay>): void {
  update((s) => ({ ...s, sankey_display: { ...s.sankey_display, ...patch } }));
  rerenderChartFromLast();
  syncToUrl();
}

/* ── Box plot ───────────────────────────────────────────── */

export function setBoxMode(mode: BoxMode): void {
  update((s) => ({ ...s, box_mode: mode }));
  syncToUrl();
}

export function addBoxCol(col: string): void {
  update((s) =>
    s.box_columns.includes(col) || s.box_columns.length >= BOX_MAX_COLUMNS
      ? s
      : { ...s, box_columns: [...s.box_columns, col] },
  );
  syncToUrl();
}

export function removeBoxCol(col: string): void {
  update((s) => ({ ...s, box_columns: s.box_columns.filter((c) => c !== col) }));
  syncToUrl();
}

/**
 * Move a box. The renderer lays boxes out in picker order, so this redraws from
 * what is loaded - the statistics do not depend on the order.
 */
export function reorderBoxCol(from: number, to: number): void {
  update((s) => {
    const list = [...s.box_columns];
    if (from < 0 || from >= list.length || to < 0 || to >= list.length) return s;
    const moved = list.splice(from, 1)[0];
    if (moved === undefined) return s;
    list.splice(to, 0, moved);
    return { ...s, box_columns: list };
  });
  rerenderChartFromLast();
  syncToUrl();
}

export function setBoxColumn(col: string): void {
  update((s) => ({ ...s, box_column: col }));
  syncToUrl();
}

export function setBoxGroupColumn(col: string): void {
  update((s) => ({ ...s, box_group_column: col }));
  syncToUrl();
}

/* ── Table ──────────────────────────────────────────────── */

export function setTableMode(mode: TableMode): void {
  update((s) => ({ ...s, table_mode: mode, table_sort: '', table_page: 0 }));
  syncToUrl();
}

export function addTableColumn(col: string): void {
  update((s) =>
    s.table_columns.includes(col) || s.table_columns.length >= TABLE_MAX_COLUMNS
      ? s
      : { ...s, table_columns: [...s.table_columns, col], table_page: 0 },
  );
  syncToUrl();
}

/** Removing a column needs no query: the table redraws without it. */
export function removeTableColumn(col: string): void {
  update((s) => ({ ...s, table_columns: s.table_columns.filter((c) => c !== col), table_sort: '' }));
  rerenderChartFromLast();
  syncToUrl();
}

/** Column order is only layout: redraw what is loaded, as for box order. */
export function reorderTableColumn(from: number, to: number): void {
  update((s) => {
    const list = [...s.table_columns];
    if (from < 0 || from >= list.length || to < 0 || to >= list.length) return s;
    const moved = list.splice(from, 1)[0];
    if (moved === undefined) return s;
    list.splice(to, 0, moved);
    // A sort names a column by position; after a move it would name another.
    return { ...s, table_columns: list, table_sort: '' };
  });
  rerenderChartFromLast();
  syncToUrl();
}

export function setTableGroups(groups: TableGroup[]): void {
  update((s) => ({ ...s, table_groups: groups, table_sort: '' }));
  syncToUrl();
}

export function setTableMeasures(measures: TableMeasureState[]): void {
  update((s) => ({ ...s, table_measures: measures.slice(0, TABLE_MAX_MEASURES) }));
  syncToUrl();
}

/**
 * Change how a measure is shown - its label or decimals - without asking the
 * server again: the table redraws from what is loaded.
 */
export function setTableMeasureDisplay(index: number, patch: Partial<Pick<TableMeasureState, 'label' | 'decimals'>>): void {
  update((s) => ({
    ...s,
    table_measures: s.table_measures.map((m, i) => (i === index ? { ...m, ...patch } : m)),
  }));
  rerenderChartFromLast();
  syncToUrl();
}

/** Sorting reorders what is loaded; nothing is fetched. */
/**
 * A summary sorts what is loaded. Readings sort on the server - over every
 * reading, not the page on screen - so the click fetches page one in the new
 * order, as the click asks.
 */
export function setTableSort(sort: string): void {
  const readings = plotState.value.table_mode === 'readings';
  update((s) => ({ ...s, table_sort: sort, table_page: readings ? 0 : s.table_page }));
  syncToUrl();
  if (readings) void doPlot();
  else rerenderChartFromLast();
}

/** Turn a readings page: fetches it. */
export function setTablePage(page: number): void {
  update((s) => ({ ...s, table_page: Math.max(0, Math.floor(page)) }));
  syncToUrl();
  void doPlot();
}

/**
 * Rows per page. From the table's own pager it fetches page one at the new
 * size; from the editor (`fetch` false) it waits for Plot like any setting.
 */
export function setTablePageSize(size: number, fetch = false): void {
  if (!TABLE_PAGE_SIZES.includes(size)) return;
  update((s) => ({ ...s, table_page_size: size, table_page: 0 }));
  syncToUrl();
  if (fetch) void doPlot();
}

/* ── Pie ────────────────────────────────────────────────── */

export function setPieMode(mode: PieMode): void {
  update((s) => ({ ...s, pie_mode: mode }));
  syncToUrl();
}

export function addPieCol(col: string): void {
  update((s) =>
    s.pie_columns.includes(col) || s.pie_columns.length >= PIE_MAX_COLUMNS
      ? s
      : { ...s, pie_columns: [...s.pie_columns, col] },
  );
  syncToUrl();
}

export function removePieCol(col: string): void {
  update((s) => ({ ...s, pie_columns: s.pie_columns.filter((c) => c !== col) }));
  syncToUrl();
}

/**
 * Move a slice. The renderer lays slices out in picker order, so this redraws
 * from what is loaded - the aggregates do not depend on the order.
 */
export function reorderPieCol(from: number, to: number): void {
  update((s) => {
    const list = [...s.pie_columns];
    if (from < 0 || from >= list.length || to < 0 || to >= list.length) return s;
    const moved = list.splice(from, 1)[0];
    if (moved === undefined) return s;
    list.splice(to, 0, moved);
    return { ...s, pie_columns: list };
  });
  rerenderChartFromLast();
  syncToUrl();
}

export function setPieAgg(agg: PieAgg): void {
  update((s) => ({ ...s, pie_agg: agg }));
  syncToUrl();
}

export function setPieBandColumn(col: string): void {
  // New signal, new range: cut points typed for the last one would be nonsense
  // on this one, so go back to choosing them from the data.
  update((s) =>
    s.pie_band_column === col ? s : { ...s, pie_band_column: col, pie_band_edges: null },
  );
  syncToUrl();
}

export function setPieBands(n: number): void {
  const v = Math.max(PIE_BANDS_MIN, Math.min(PIE_BANDS_MAX, Math.round(n) || PIE_BANDS_DEFAULT));
  update((s) => ({ ...s, pie_bands: v }));
  syncToUrl();
}

/**
 * Parse typed cut points: numbers separated by commas or spaces. Returns null
 * for an empty entry (meaning "choose from the data") and a message for input
 * that cannot be used, so the field can say what is wrong rather than guess.
 */
export function parseBandEdges(text: string): { edges: number[] | null; error: string | null } {
  const parts = text
    .split(/[,;\s]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return { edges: null, error: null };
  const nums = parts.map(Number);
  if (nums.some((n) => !Number.isFinite(n))) {
    return { edges: null, error: 'Cut points must be numbers' };
  }
  const sorted = [...new Set(nums)].sort((a, b) => a - b);
  if (sorted.length > PIE_BANDS_MAX - 1) {
    return { edges: null, error: `At most ${PIE_BANDS_MAX - 1} cut points` };
  }
  return { edges: sorted, error: null };
}

export function setPieBandEdges(edges: number[] | null): void {
  update((s) => ({ ...s, pie_band_edges: edges && edges.length ? edges : null }));
  syncToUrl();
}

export function setHistBins(n: number): void {
  const bins = Math.max(HIST_BINS_MIN, Math.min(HIST_BINS_MAX, Math.round(n) || 40));
  update((s) => ({ ...s, hist_bins: bins }));
  syncToUrl();
}

/* ── Style toggles (line) + scatter style ───────────────── */

export function setStyleField<K extends keyof PlotStyle>(key: K, value: PlotStyle[K]): void {
  update((s) => ({ ...s, style: { ...s.style, [key]: value } }));
  if (REMEMBERED_STYLE_KEYS.has(key)) remember(key, value);
  rerenderChartFromLast();
  syncToUrl();
}

export function setLineWidth(v: number): void {
  const w = Math.max(LINE_WIDTH_MIN, Math.min(LINE_WIDTH_MAX, Math.round(v)));
  setStyleField('line_width', w);
}

export function setPointSize(v: number): void {
  const sz = Math.max(POINT_SIZE_MIN, Math.min(POINT_SIZE_MAX, Math.round(v)));
  setStyleField('scatter_point_size', sz);
}

/* ── Curve fits (§10.8) ─────────────────────────────────── */

/** Set (or clear, with `null`) the single fit attached to a series. */
export function setSeriesFit(series: string, entry: Fit | null): void {
  update((s) => ({
    ...s,
    fits: [...s.fits.filter((f) => f.series !== series), ...(entry ? [entry] : [])],
  }));
  syncToUrl();
}

/* ── Overlays ───────────────────────────────────────────── */

export function setShowLegs(on: boolean): void {
  update((s) => ({ ...s, show_legs: on }));
  syncToUrl();
  // Fetch + draw straight away so the toggle takes effect without re-plotting;
  // maybeFetch no-ops if no chart is drawn yet (the next doPlot picks it up).
  if (on) void maybeFetchLegsAndRender();
  else clearLegsOverlay();
}

export function setShowChangeovers(on: boolean): void {
  update((s) => ({ ...s, show_changeovers: on }));
  syncToUrl();
  if (on) void maybeFetchChangeoversAndRender();
  else clearChangeoversOverlay();
}

/* ── Axis bounds ────────────────────────────────────────── */

export type AxisBoundKey = keyof AxisBounds;

export function setAxisBound(key: AxisBoundKey, value: number | null): void {
  update((s) => ({ ...s, axis_bounds: { ...s.axis_bounds, [key]: value } }));
  syncToUrl();
}

export function axisBoundsActiveCount(s: PlotState = plotState.value): number {
  return (Object.keys(s.axis_bounds) as AxisBoundKey[]).filter(
    (k) => s.axis_bounds[k] != null,
  ).length;
}

export function setAxisTitle(axis: keyof AxisTitles, patch: Partial<AxisTitle>): void {
  update((s) => ({
    ...s,
    axis_titles: { ...s.axis_titles, [axis]: { ...s.axis_titles[axis], ...patch } },
  }));
  syncToUrl();
}

export function axisTitlesActiveCount(s: PlotState = plotState.value): number {
  return (['y', 'yr'] as const).filter((k) => s.axis_titles[k].mode !== 'auto').length;
}

/* ── Derived-signal cascades (§10.4 → §10.7 seam, now complete) ── */

/** Strip every reference to a removed derived name from the plot state. */
export function stripDerivedFromPlot(name: string): void {
  update((s) => {
    const customColors = { ...s.custom_colors };
    delete customColors[name];
    return {
      ...s,
      x_column: s.x_column === name ? timeColumn() : s.x_column,
      y_column: s.y_column === name ? '' : s.y_column,
      z_column: s.z_column === name ? '' : s.z_column,
      number_column: s.number_column === name ? '' : s.number_column,
      number_expression: rewriteNumberSignal(s.number_expression, name, null),
      gauge_dials: rewriteGaugeSignal(s.gauge_dials, name, null),
      sankey_nodes: rewriteSankeySignal(s.sankey_nodes, name, null),
      y_left: s.y_left.filter((c) => c !== name),
      y_right: s.y_right.filter((c) => c !== name),
      color_by: s.color_by === name ? '' : s.color_by,
      hist_columns: s.hist_columns.filter((c) => c !== name),
      fits: s.fits.filter((f) => f.series !== name),
      ...stripFromTable(s.table_groups, s.table_measures, name, s.table_columns),
      custom_colors: customColors,
    };
  });
}

/** Rewrite every reference from `oldName` to `newName` after a rename. */
export function renameDerivedInPlot(oldName: string, newName: string): void {
  if (oldName === newName) return;
  update((s) => {
    const customColors = { ...s.custom_colors };
    const oldColor = customColors[oldName];
    if (oldColor !== undefined) {
      customColors[newName] = oldColor;
      delete customColors[oldName];
    }
    return {
      ...s,
      x_column: s.x_column === oldName ? newName : s.x_column,
      y_column: s.y_column === oldName ? newName : s.y_column,
      z_column: s.z_column === oldName ? newName : s.z_column,
      number_column: s.number_column === oldName ? newName : s.number_column,
      number_expression: rewriteNumberSignal(s.number_expression, oldName, newName),
      gauge_dials: rewriteGaugeSignal(s.gauge_dials, oldName, newName),
      sankey_nodes: rewriteSankeySignal(s.sankey_nodes, oldName, newName),
      y_left: s.y_left.map((c) => (c === oldName ? newName : c)),
      y_right: s.y_right.map((c) => (c === oldName ? newName : c)),
      color_by: s.color_by === oldName ? newName : s.color_by,
      hist_columns: s.hist_columns.map((c) => (c === oldName ? newName : c)),
      fits: s.fits.map((f) => (f.series === oldName ? { ...f, series: newName } : f)),
      ...renameInTable(s.table_groups, s.table_measures, oldName, newName, s.table_columns),
      custom_colors: customColors,
    };
  });
}

/* ── Reset (used by presets / §10.11) ───────────────────── */

/**
 * Reset the plot configuration to defaults (blob `resetPlotStateToDefaults`),
 * with the X column matching the current data source. Form-control resets the
 * blob also did are unnecessary here - every control renders from this atom.
 */
export function resetPlotStateToDefaults(): void {
  clearPeriodComparison();
  // Their defaults, not the code's: resetting a chart should not also undo how
  // they have decided charts should look.
  plotState.set({ ...seededPlotState(), x_column: timeColumn() });
}
